// File parsing utilities for CSV and Excel files
import * as XLSX from 'xlsx';

// Column mapping for different registration types
// These map CSV column headers to internal field names used by dataProcessor.js
export const COLUMN_MAPPINGS = {
  Student: {
    // Student personal details
    'Student First Name': 'studentFirstName',
    'Student Last Name': 'studentLastName',
    'Class': 'class',
    
    // Parent/Guardian details
    'Parent First Name': 'parentFirstName',
    'Parent Last Name': 'parentLastName',
    'Parent Mobile': 'parentMobile',
    'Parent Email': 'parentEmail',
    
    // Address details
    'Address': 'address',
    'Area': 'area',
    'City': 'city',
    
    // NEW: Bus stop details matching individual registration form structure
    // Morning Pickup: From (home) → To (school)
    // Afternoon Drop: From (school) → To (home)
    'Pickup From': 'pickupFrom',    // Home location for morning pickup
    'Pickup To': 'pickupTo',        // School name (auto-filled)
    'Drop From': 'dropFrom',        // School name (auto-filled)
    'Drop To': 'dropTo',            // Home location for afternoon drop
    
    // Country code
    'Country Code': 'countryCode'
  },
  Employee: {
    'First Name': 'firstName',
    'Last Name': 'lastName',
    'Department': 'department',
    'Address': 'address',
    'City': 'city',
    'Area': 'area',
    'Pickup Stop': 'pickupStop',
    'Drop Stop': 'dropStop',
    'Mobile Number (9 digits)': 'mobileNumber',
    'Country Code': 'countryCode'
  }
};

// Helper function to parse CSV line with proper comma handling
const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];

    if (char === '"') {
      // Handle escaped quotes (double quotes)
      if (i + 1 < line.length && line[i + 1] === '"') {
        current += '"';
        i += 2; // Skip both quotes
      } else {
        inQuotes = !inQuotes;
        i++;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
      i++;
    } else {
      current += char;
      i++;
    }
  }

  result.push(current.trim());

  // Remove surrounding quotes from each field
  return result.map(value => {
    // Remove leading and trailing quotes if they exist
    if (value.startsWith('"') && value.endsWith('"')) {
      return value.slice(1, -1);
    }
    return value;
  });
};

// Parse CSV file
export const parseCSVFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim() !== '');

        if (lines.length < 2) {
          reject(new Error('File must contain at least a header row and one data row'));
          return;
        }

        const headers = parseCSVLine(lines[0]);
        const data = [];

        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          if (values.length > 0) {
            const row = {};
            headers.forEach((header, index) => {
              row[header.trim()] = values[index]?.trim() || '';
            });
            data.push({ rowIndex: i + 1, data: row });
          }
        }

        resolve(data);
      } catch (error) {
        reject(new Error(`Error parsing CSV: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };

    reader.readAsText(file);
  });
};

// Parse Excel file
export const parseExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const arrayBuffer = new Uint8Array(e.target.result);
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });

        // Use the first worksheet
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];

        // Convert to JSON with header row
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
          blankrows: false
        });

        if (jsonData.length < 2) {
          reject(new Error('File must contain at least a header row and one data row'));
          return;
        }

        const headers = jsonData[0].map(header => String(header).trim());
        const parsedRows = [];

        for (let i = 1; i < jsonData.length; i++) {
          const row = {};
          headers.forEach((header, index) => {
            row[header] = String(jsonData[i][index] || '').trim();
          });
          parsedRows.push({ rowIndex: i + 1, data: row });
        }

        resolve(parsedRows);
      } catch (error) {
        reject(new Error(`Error parsing Excel file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };

    reader.readAsArrayBuffer(file);
  });
};

// Map parsed data to internal field names
export const mapDataToFields = (data, type) => {
  const mapping = COLUMN_MAPPINGS[type];
  if (!mapping) {
    throw new Error(`Unknown registration type: ${type}`);
  }

  return data.map(item => {
    const mappedData = {};
    Object.keys(item.data).forEach(key => {
      const mappedKey = mapping[key];
      if (mappedKey) {
        mappedData[mappedKey] = item.data[key];
      }
    });

    return {
      ...item,
      data: mappedData
    };
  });
};